import React, { useEffect, useMemo, useState } from 'react';
import { ArrowRight, Loader2, Newspaper, Search, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Layout } from './Home';
import { listCompanyNews, resolveFileUrl } from '../services/api';

const formatDate = (raw) => {
  if (!raw) return '';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const NewsroomListing = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');

  useEffect(() => {
    listCompanyNews(100)
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (n) =>
        n.title?.toLowerCase().includes(q) ||
        n.short_desc?.toLowerCase().includes(q) ||
        n.long_desc?.toLowerCase().includes(q),
    );
  }, [items, query]);

  const [featured, ...rest] = filtered;

  return (
    <Layout>
      <section className="bg-[#e2ede6] pt-14 pb-20 lg:pt-20 lg:pb-28" data-testid="newsroom-page">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
            <div>
              <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.25em] text-[#0f5c3d] uppercase">
                <Newspaper className="w-4 h-4" /> Emerald Newsroom
              </span>
              <h1
                className="mt-3 text-5xl lg:text-6xl text-[#0b1a2e] leading-[1.02]"
                style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}
              >
                Press & Corporate News
              </h1>
              <p className="mt-4 text-[#0b1a2e]/70 max-w-2xl leading-relaxed">
                Announcements, deal news, milestones and independent media coverage of the Emerald Corporates group.
              </p>
            </div>
            <div className="relative w-full md:w-80">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#0b1a2e]/50" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search press releases…"
                data-testid="newsroom-search-input"
                className="w-full bg-white border border-[#0f5c3d]/15 rounded-full pl-11 pr-10 py-3 text-sm outline-none focus:ring-2 focus:ring-[#0f5c3d]/30"
              />
              {query && (
                <button
                  onClick={() => setQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-[#0f5c3d]/10 hover:bg-[#0f5c3d]/20 text-[#0f5c3d] flex items-center justify-center"
                  aria-label="clear search"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-24 text-[#0b1a2e]/60">
              <Loader2 className="w-6 h-6 animate-spin mr-3" /> Loading corporate news…
            </div>
          ) : filtered.length === 0 ? (
            <div className="bg-white border border-black/5 p-10 rounded-xl text-center text-[#0b1a2e]/60">
              {query ? 'No articles match your search.' : 'No corporate news published yet.'}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featured && (
                <Link
                  to={`/newsroom/${featured.slug}`}
                  data-testid="newsroom-featured-card"
                  className="group md:col-span-2 lg:col-span-2 md:row-span-2 bg-white rounded-2xl overflow-hidden border border-black/5 shadow-sm hover:shadow-xl transition-all"
                >
                  <div className="relative aspect-[16/9] overflow-hidden bg-[#e2ede6]">
                    {featured.image_url ? (
                      <img
                        src={resolveFileUrl(featured.image_url)}
                        alt={featured.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-tr from-[#0b1a2e] to-[#0f5c3d]" />
                    )}
                  </div>
                  <div className="p-6 lg:p-8">
                    <div className="text-xs font-semibold tracking-widest uppercase text-[#0f5c3d]">
                      {formatDate(featured.published_at)}
                    </div>
                    <h2 className="mt-3 text-3xl text-[#0b1a2e] leading-tight group-hover:text-[#0f5c3d] transition"
                        style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}>
                      {featured.title}
                    </h2>
                    <p className="mt-3 text-[#0b1a2e]/70 leading-relaxed line-clamp-3">
                      {featured.short_desc}
                    </p>
                    <div className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-[#0f5c3d]">
                      Read press release <ArrowRight className="w-4 h-4" />
                    </div>
                  </div>
                </Link>
              )}
              {rest.map((n) => (
                <Link
                  key={n.id}
                  to={`/newsroom/${n.slug}`}
                  data-testid="newsroom-card"
                  className="group bg-white rounded-2xl overflow-hidden border border-black/5 shadow-sm hover:shadow-xl transition-all"
                >
                  <div className="relative aspect-[16/10] overflow-hidden bg-[#e2ede6]">
                    {n.image_url ? (
                      <img
                        src={resolveFileUrl(n.image_url)}
                        alt={n.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-tr from-[#0b1a2e] to-[#0f5c3d]" />
                    )}
                  </div>
                  <div className="p-5">
                    <div className="text-xs font-semibold tracking-widest uppercase text-[#0f5c3d]">
                      {formatDate(n.published_at)}
                    </div>
                    <h3 className="mt-2 text-[17px] font-semibold leading-snug text-[#0b1a2e] line-clamp-2 group-hover:text-[#0f5c3d] transition">
                      {n.title}
                    </h3>
                    <p className="mt-2 text-sm text-[#0b1a2e]/60 line-clamp-3">
                      {n.short_desc}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default NewsroomListing;
